import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, 
  Target, 
  Megaphone, 
  Lightbulb, 
  MessageSquare, 
  Video, 
  FileText,
  Calendar,
  Eye,
  Loader2,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const BusinessContentView = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contentData, setContentData] = useState(null);

  useEffect(() => {
    loadFullContent();
  }, [businessId]);

  const loadFullContent = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/businesses/${businessId}/full-content`);
      setContentData(response.data);
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Error al cargar contenido de la empresa');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-slate-600">Cargando contenido de la empresa...</p>
        </div>
      </div>
    );
  }

  if (!contentData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-slate-600">No se pudo cargar el contenido</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Volver al Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { business, icp, statistics } = contentData;

  const contentSections = [
    {
      title: 'Perfil de Cliente Ideal (ICP)',
      icon: Target,
      color: 'blue',
      count: icp ? 1 : 0,
      items: icp ? [icp] : [],
      emptyMessage: 'No hay ICP generado aún',
      viewAction: () => navigate('/icp')
    },
    {
      title: 'Campañas',
      icon: Megaphone,
      color: 'purple',
      count: statistics.total_campaigns,
      items: contentData.campaigns,
      emptyMessage: 'No hay campañas generadas',
      viewAction: () => navigate('/campaigns')
    },
    {
      title: 'Anuncios (Ads)',
      icon: Lightbulb,
      color: 'orange',
      count: statistics.total_ads,
      items: contentData.ads,
      emptyMessage: 'No hay anuncios generados',
      viewAction: () => navigate('/ads')
    },
    {
      title: 'Scripts de Video',
      icon: Video,
      color: 'red',
      count: statistics.total_scripts,
      items: contentData.scripts,
      emptyMessage: 'No hay scripts generados',
      viewAction: () => navigate('/scripts')
    },
    {
      title: 'CTAs',
      icon: MessageSquare,
      color: 'green',
      count: statistics.total_ctas,
      items: contentData.ctas,
      emptyMessage: 'No hay CTAs generados',
      viewAction: () => navigate('/ctas')
    },
    {
      title: 'Contenido Educativo',
      icon: FileText,
      color: 'indigo',
      count: statistics.total_content,
      items: contentData.content,
      emptyMessage: 'No hay contenido generado',
      viewAction: () => navigate('/content-creator')
    },
    {
      title: 'Planificador 3-3-3',
      icon: Calendar,
      color: 'teal',
      count: statistics.total_planner_projects,
      items: contentData.planner_333_projects,
      emptyMessage: 'No hay proyectos de planificación',
      viewAction: () => navigate('/planner-333')
    },
    {
      title: 'Proyectos MÓNICA',
      icon: Video,
      color: 'pink',
      count: statistics.total_monica_projects,
      items: contentData.monica_projects,
      emptyMessage: 'No hay proyectos MÓNICA',
      viewAction: () => navigate('/monica-wizard')
    }
  ];

  const totalItems = Object.values(statistics).reduce((sum, val) => sum + val, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900">Todo el Contenido de tu Empresa</h1>
              <p className="text-sm text-slate-600 mt-1">
                {business.business_description}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Card */}
        <Card className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-purple-600" />
              Resumen de Contenido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-purple-600">{totalItems}</p>
                <p className="text-sm text-slate-600 mt-1">Total Elementos</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-blue-600">{statistics.total_campaigns}</p>
                <p className="text-sm text-slate-600 mt-1">Campañas</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-orange-600">{statistics.total_ads}</p>
                <p className="text-sm text-slate-600 mt-1">Anuncios</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-pink-600">{statistics.total_monica_projects}</p>
                <p className="text-sm text-slate-600 mt-1">Proyectos MÓNICA</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-white rounded-lg border border-purple-200">
              <h4 className="font-semibold text-sm text-slate-700 mb-2">Información de la Empresa:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <span className="font-semibold text-slate-600">Producto/Servicio:</span>
                  <p className="text-slate-700">{business.product_service}</p>
                </div>
                <div>
                  <span className="font-semibold text-slate-600">Precio:</span>
                  <p className="text-slate-700">{business.price}</p>
                </div>
                <div>
                  <span className="font-semibold text-slate-600">Audiencia:</span>
                  <p className="text-slate-700">{business.target_audience}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Sections */}
        <div className="grid gap-6">
          {contentSections.map((section, idx) => {
            const Icon = section.icon;
            const colorClasses = {
              blue: 'from-blue-500 to-cyan-500',
              purple: 'from-purple-500 to-pink-500',
              orange: 'from-orange-500 to-red-500',
              red: 'from-red-500 to-pink-500',
              green: 'from-green-500 to-emerald-500',
              indigo: 'from-indigo-500 to-purple-500',
              teal: 'from-teal-500 to-cyan-500',
              pink: 'from-pink-500 to-rose-500'
            };

            return (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-lg bg-gradient-to-r ${colorClasses[section.color]}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{section.title}</CardTitle>
                        <CardDescription>
                          {section.count > 0 
                            ? `${section.count} elemento${section.count !== 1 ? 's' : ''} generado${section.count !== 1 ? 's' : ''}`
                            : section.emptyMessage
                          }
                        </CardDescription>
                      </div>
                    </div>
                    <Button
                      onClick={section.viewAction}
                      variant="outline"
                      size="sm"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ir a Módulo
                    </Button>
                  </div>
                </CardHeader>
                
                {section.count > 0 && (
                  <CardContent>
                    <div className="space-y-3">
                      {section.items.slice(0, 3).map((item, itemIdx) => (
                        <div 
                          key={itemIdx}
                          className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                        >
                          {/* ICP */}
                          {section.title === 'Perfil de Cliente Ideal (ICP)' && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700">
                                {item.demographics?.age_range} • {item.demographics?.gender}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                Pain Point: {item.pain_points?.[0]}
                              </p>
                            </div>
                          )}

                          {/* Campaigns */}
                          {section.title === 'Campañas' && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700">{item.campaign_name}</p>
                              <p className="text-xs text-slate-600 mt-1">
                                {item.platform} • {item.objective}
                              </p>
                            </div>
                          )}

                          {/* Ads */}
                          {section.title === 'Anuncios (Ads)' && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700 line-clamp-1">
                                {item.headline || item.primary_text}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                {item.platform} • {item.format}
                              </p>
                            </div>
                          )}

                          {/* Scripts */}
                          {section.title === 'Scripts de Video' && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700">
                                {item.hook || 'Script de video'}
                              </p>
                              <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                                {item.body?.substring(0, 100)}...
                              </p>
                            </div>
                          )}

                          {/* CTAs */}
                          {section.title === 'CTAs' && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700 line-clamp-1">
                                {item.cta_text}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                {item.cta_type}
                              </p>
                            </div>
                          )}

                          {/* Content */}
                          {section.title === 'Contenido Educativo' && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700 line-clamp-1">
                                {item.title || item.topic}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                {item.content_type} • {item.platform}
                              </p>
                            </div>
                          )}

                          {/* Planner 3-3-3 */}
                          {section.title === 'Planificador 3-3-3' && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700">
                                Proyecto: {new Date(item.created_at).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                Estado: {item.status}
                              </p>
                            </div>
                          )}

                          {/* MÓNICA Projects */}
                          {section.title === 'Proyectos MÓNICA' && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700 line-clamp-1">
                                {item.main_idea}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                {item.video_duration}s • Estado: {item.status}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {section.count > 3 && (
                        <p className="text-xs text-slate-500 text-center pt-2">
                          + {section.count - 3} más
                        </p>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BusinessContentView;
